import React, {useState, useEffect} from 'react';
import {
  Card,
  Button,
  Typography,
  IconButton,
  TextField,
  Box,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import Rating from '@mui/material/Rating';
import './dragdrop.css'; // Import the CSS file

interface Choice {
  id: number;
  value: string;
}

interface MatchChoice {
  id: number;
  Question: string;
  value: string;
}

interface CustomIcon {
  icon: React.ReactElement;
  label: string;
  id: number;
  key: string;
}
interface CustomIcons {
  [key: string]: CustomIcon;
}
interface RatingScaleConfig {
  ratingScale: number;
  ratingScaleType: string;
  customIcons: CustomIcons;
}
interface npsString {
  likely: string;
  notlikely: string;
}

interface BaseQuestion {
  id: string;
  question: string;
  mark?: number;
  questionType:
    | 'single'
    | 'multiple'
    | 'true_false'
    | 'match'
    | 'descriptive'
    | 'ratingscale'
    | 'star'
    | 'nps';
  category?: string;
  level?: string;
  tags?: string;
  addToBank?: boolean;
  disableComments?: boolean;
  makecommentmandatory?: boolean;
  markasMandatory?: boolean;
}

interface SingleQuestion extends BaseQuestion {
  questionType: 'single';
  correctAnswer?: number;
  choices: Choice[];
}

interface MultipleQuestion extends BaseQuestion {
  questionType: 'multiple';
  correctAnswers?: number[];
  choices: Choice[];
}

interface TrueFalseQuestion extends BaseQuestion {
  questionType: 'true_false';
  answer: string;
}

interface MatchingQuestion extends BaseQuestion {
  questionType: 'match';
  correctMatches: MatchChoice[];
  additionalChoices: MatchChoice[];
}

interface DescriptiveQuestion extends BaseQuestion {
  questionType: 'descriptive';
  enforceWordLimit: boolean;
  wordLimit?: number;
}
interface RatingScaleQuestion extends BaseQuestion {
  questionType: 'ratingscale';
  ratingScale: RatingScaleConfig;
}
interface StarQuestion extends BaseQuestion {
  questionType: 'star';
  starRating?: number;
}
interface NPSQuestion extends BaseQuestion {
  questionType: 'nps';
  npsstring: npsString;
}

type Question =
  | SingleQuestion
  | MultipleQuestion
  | TrueFalseQuestion
  | MatchingQuestion
  | DescriptiveQuestion
  | RatingScaleQuestion
  | NPSQuestion
  | StarQuestion;

interface DragdropTypeProps {
  questiondata: Question[];
  handleDeleteQuestion?: (value: string) => void;
  handleEditQuestion?: (value: any) => void;
}

const DragDropContentDisplay: React.FC<DragdropTypeProps> = ({
  questiondata,
  handleDeleteQuestion,
  handleEditQuestion
}) => {
  const [questions, setQuestions] = useState<Question[]>(questiondata);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const [newQuestionText, setNewQuestionText] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null
  );

  useEffect(() => {
    setQuestions(questiondata);
  }, [questiondata]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: any) => {
    const {active, over} = event;
    if (active.id !== over.id) {
      setQuestions(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleQuestionNumberChange = (fromIndex: number, toIndex: string) => {
    const toIndexParsed = parseInt(toIndex);
    const toIndexCorrected = toIndexParsed - 1;
    if (
      fromIndex < 0 ||
      fromIndex >= questions.length ||
      toIndexCorrected < 0 ||
      toIndexCorrected >= questions.length
    ) {
      throw new Error('Index out of bounds');
    }

    const [movedObject] = questions.splice(fromIndex, 1);
    questions.splice(toIndexCorrected, 0, movedObject);

    setQuestions([...questions]);
  };

  const handleQuestionTextChange = (id: string, newText: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(question =>
        question.id === id ? {...question, question: newText} : question
      )
    );
  };

  const SortableItem: React.FC<{id: string; children: any}> = ({
    id,
    children
  }) => {
    const {attributes, listeners, setNodeRef, transform, transition} =
      useSortable({id});
    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    return (
      <div ref={setNodeRef} style={style}>
        {children({attributes, listeners})}
      </div>
    );
  };

  // const handleEditQuestion = (question: any) => {
  //   setEditingQuestionId(id);
  //   const question = questions.find(q => q.id === id);
  //   if (question) setNewQuestionText(question.question);
  // };

  const handleSaveQuestion = () => {
    if (editingQuestionId) {
      handleQuestionTextChange(editingQuestionId, newQuestionText);
      setEditingQuestionId(null);
      setNewQuestionText('');
    }
  };

  const handleQuestionId = (id: string) => {
    if (selectedQuestionId === id) {
      setSelectedQuestionId(null);
    } else {
      setSelectedQuestionId(id);
    }
  };
  const getIconsForScale = (scale: number, type: any) => {
    const icons = type;
    switch (scale) {
      case 3:
        return [icons[1], icons[4], icons[7]];
      case 5:
        return [icons[1], icons[2], icons[4], icons[5], icons[7]];
      case 7:
        return [
          icons[1],
          icons[2],
          icons[3],
          icons[4],
          icons[5],
          icons[6],
          icons[7]
        ];
      default:
        return [];
    }
  };
  const getColor = (index: number) => {
    if (index <= 6) return '#f44336'; // Red
    if (index === 7 || index === 8) return '#ffeb3b'; // Yellow
    return '#4caf50'; // Green
  };

  const renderQuestionOptions = (question: Question) => {
    switch (question.questionType) {
      case 'single':
        return question.choices.map((option, index) => (
          <FormControlLabel
            key={index}
            control={<Radio checked={question.correctAnswer === index + 1} />}
            label={option.value}
          />
        ));
      case 'multiple':
        return question.choices.map((option, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={
                  question.correctAnswers &&
                  question?.correctAnswers.includes(index + 1)
                }
              />
            }
            label={option.value}
          />
        ));
      case 'true_false':
        return (
          <RadioGroup value={question.answer}>
            {['True', 'False'].map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio checked={question.answer === option} />}
                label={option}
              />
            ))}
          </RadioGroup>
        );
      case 'match':
        return (
          <>
            {question.correctMatches.map((match, index) => (
              <div
                key={match.id}
                style={{display: 'flex', alignItems: 'center'}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={match.Question}
                  sx={{mb: 1, mr: 1}}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  value={match.value}
                  sx={{mb: 1}}
                />
              </div>
            ))}
            {question?.additionalChoices?.length > 0 && (
              <Typography variant="h6" style={{marginTop: 10}}>
                Additional Choices
              </Typography>
            )}
            {question.additionalChoices &&
              question.additionalChoices.map((choice, index) => (
                <div
                  key={choice.id}
                  style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={choice.value}
                    sx={{mb: 1, width: '49%'}}
                  />
                </div>
              ))}
          </>
        );
      case 'descriptive':
        return (
          <>
            {question.enforceWordLimit && (
              <>
                <Typography variant="h6" style={{marginTop: 10}}>
                  Enforce word limit
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  value={question.wordLimit}
                  sx={{mb: 2}}
                />
              </>
            )}
          </>
        );
      case 'ratingscale':
        return (
          <>
            <Box sx={{display: 'flex', justifyContent: 'space-around', mt: 2}}>
              {getIconsForScale(
                question.ratingScale?.ratingScale,
                question.ratingScale?.customIcons
              ).map(icon => (
                <Box key={icon.id} sx={{textAlign: 'center'}}>
                  {icon.icon}
                </Box>
              ))}
            </Box>
          </>
        );
      case 'star':
        return (
          <Box sx={{paddingLeft: '4vh'}}>
            <Rating
              name="star-rating"
              value={question.starRating}
              // onChange={(event, newValue) => {
              //   setRating(newValue);
              // }}
              max={question.starRating}
              readOnly
              size="medium"
            />
          </Box>
        );
      case 'nps':
        return (
          <>
            <Box>
              <ToggleButtonGroup
                exclusive
                aria-label="answer score"
                fullWidth
                disabled
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  flexWrap: 'wrap'
                }}>
                {[...Array(11)].map((_, index) => (
                  <ToggleButton
                    key={index}
                    value={index}
                    sx={{
                      flex: '1 0 9%',
                      backgroundColor: getColor(index),
                      color: 'black !important',
                      maxWidth: '55px',
                    }}>
                    {index}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1
              }}>
              <p>{question.npsstring.likely}</p>
              <p>{question.npsstring.notlikely}</p>
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext
          items={questions}
          strategy={verticalListSortingStrategy}>
          {questions.map((question, index) => (
            <SortableItem key={question.id} id={question.id}>
              {({ attributes, listeners }: any) => (
                <Box
                  className="card-container"
                >
                  <Box className="header">
                    <div className="header-content">
                      <Box
                        {...listeners}
                        {...attributes}
                        className="drag-handle">
                        <DragIndicatorIcon />
                        <TextField
                          defaultValue={(index + 1).toString()}
                          onBlur={e =>
                            handleQuestionNumberChange(index, e.target.value)
                          }
                          style={{ width: 30, marginLeft: 10 }}
                          inputProps={{
                            style: { textAlign: 'center', padding: '4px' }
                          }}
                        />
                      </Box>
                      {editingQuestionId === question.id ? (
                        <TextField
                          fullWidth
                          value={newQuestionText}
                          onChange={e => setNewQuestionText(e.target.value)}
                          style={{ marginRight: 10 }}
                        />
                      ) : (
                        <Typography
                          className="question-text"
                          onClick={() => handleQuestionId(question.id)}>
                          {question.question}
                        </Typography>
                      )}
                    </div>
                    <div className="actions">
                      <Typography variant="caption" style={{ marginRight: 10 }}>
                        {question.mark}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        {editingQuestionId === question.id ? (
                          <Button
                            onClick={handleSaveQuestion}
                            variant="contained"
                            color="primary">
                            Save
                          </Button>
                        ) : (
                          <IconButton
                            onClick={() =>
                              handleEditQuestion && handleEditQuestion(question)
                            }>
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() =>
                            handleDeleteQuestion &&
                            handleDeleteQuestion(question.id)
                          }>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </div>
                  </Box>
                  {selectedQuestionId === question.id && (
                    <Box className="options-container">
                      {renderQuestionOptions(question)}
                    </Box>
                  )}
                </Box>
              )}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
    </Card>
  );
};

export default DragDropContentDisplay;
